import store from 'store'
import i18n from 'i18n'
import orgChartDB from 'libs/DB/orgChartDB'
import { isGlobal } from 'conf'
import history from 'history'
import { message } from 'antd'
import { deleteUMKFromLocal, RSAEncDec } from 'pUtils'

import SettingsService from 'popups/Settings/api/Settings'
import { clearClipboardNow } from 'utils/clipboardClear'
import { errorBase, openloadingAni, confirmBase } from 'utils/ui'

import { store as reduxStore } from 'index'
import {
  getEnvParam,
  removeEnvParam,
  setTempEnvParam,
  getTempEnvParam,
  removeTempEnvParam,
} from 'env'

import { authClient, authClientRequiredToken, officeNextClient } from 'myNet'

import { handleAuthSuccess, saveTokenAndDispatchLogin, handleAuthError } from 'utils/auth-callback'
import { clearAll as clearTotpDuplicateRegistry } from 'services/totp/duplicateRegistry'
import { clearMemoDraft } from 'layouts/Main/components/CreateMemoModal/memoDraft'
import { removeLocalAppUserKeys } from 'utils/localUserKeys'

const prevLandingInfo = {}
const appConfigMap = {}

const removeAllAppUserKeys = () => {
  // TOTP 중복 감지 지문 캐시 클리어(#1506) — 계정 전환 시 이전 계정 지문 잔존 방지.
  // 모든 로그아웃 경로(다중 탭 broadcast 포함)가 이 함수를 거치는 chokepoint.
  clearTotpDuplicateRegistry()
  // 메모 임시저장(초안, sessionStorage) 클리어 — 같은 탭에서 계정 전환 시 이전 계정의
  // 메모 초안이 복원 팝업으로 노출되지 않도록. (removeAllAppUserKeys 는 localStorage 만 훑음)
  clearMemoDraft()
  removeLocalAppUserKeys()
}

export function appConfig(email) {
  const promise = new Promise((resolve, reject) => {
    const info = {}
    info.auth = 'oauth'

    const landingOfficemail = () => {
      const landingType =
        process.env.REACT_APP_TARGET === 'korea-staging' ? 'staging-safebox' : 'safebox'
      const provider = 'officenext'
      const language = (store.get('app.settings.locale') || '').substring(0, 2)
      const urlParams = new URLSearchParams(window.location.search)
      const path = urlParams.get('path')

      const params = {
        login_hint: email,
        response_mode: 'redirect',
        schema: 'web',
      }
      if (path) {
        params.return_path = path
      }

      authClient
        .get(`/auth/oauth/authorize/${landingType}/${provider}`, params)
        .then((landingRes) => {
          console.log('landing - ', landingType, landingRes.data.data)
          const { authorization_url: authUrl } = landingRes.data.data

          if (landingRes.data.code === 200) {
            window.location.href = `${authUrl}&lo=${language}`
            resolve(info)
            console.log(' ---  info.authUrl - ', info.authUrl)
          } else {
            reject(new Error('landing error.'))
          }
        })
    }

    landingOfficemail()
  })

  return promise
}

const getLandingWindowSize = () => {
  return {
    width: Math.min(window.screen.availWidth, 1024),
    height: Math.min(window.screen.availHeight, 711),
  }
}

export function openLandingPopup(smallSize) {
  const clearPrevLandingEvent = () => {
    if (prevLandingInfo.clearLandingEvent != null) {
      prevLandingInfo.clearLandingEvent()
    }
  }

  clearPrevLandingEvent()

  const windowSize = getLandingWindowSize()
  const windowLocation = {
    left: window.screen.availLeft + window.screen.availWidth / 2 - windowSize.width / 2,
    top: window.screen.availTop + window.screen.availHeight / 2 - windowSize.height / 2,
  }

  const options =
    smallSize !== true
      ? `width=${windowSize.width},height=${windowSize.height},left=${windowLocation.left},top=${windowLocation.top}`
      : `width=${10},height=${10},left=${windowLocation.left},top=${windowLocation.top}`

  window.loginWin = window.open('', `landingWin`, options)

  if (window.loginWin == null) {
    errorBase({
      title: i18n.t('common.title.blockPopup'),
      content: i18n.t('common.warning.blockPopup'),
    })

    reduxStore.dispatch({
      type: 'user/LOGOUT',
    })

    return false
  }

  return true
}

export function closeLandingPopup() {
  try {
    if (window.loginWin != null) {
      if (window.loginTimer != null) {
        clearInterval(window.loginTimer)
      }
      window.loginWin.close()
      window.loginWin = null
    }
  } catch (err) {
    console.log(err)
  }
}

export function parsedJWT(token) {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(''),
  )

  const { email } = JSON.parse(jsonPayload)

  return email
}

function deleteUMKInfo() {
  deleteUMKFromLocal()
  const rsaEncDec = getTempEnvParam('rsaObj')
  if (rsaEncDec != null) {
    rsaEncDec.clear()
  }

  removeTempEnvParam('rsaObj')
  removeTempEnvParam('base64privateKey')
  removeTempEnvParam('decryptedUMK')
  removeTempEnvParam('encryptedUMK')
  removeTempEnvParam('base64recoveryPublicKey')
  removeTempEnvParam('recoveryPubKeyOwner')
}

export function performSsoLogout(onAfterLogout) {
  const syncAniObj = openloadingAni({
    text: i18n.t('logOut.desc.progress') || 'Loading...',
  })

  const headers = {
    Accept: 'application/json',
  }

  Promise.resolve(officeNextClient.post('/api/logout', {}, { headers }))
    .then(async () => {
      deleteUMKInfo()

      await orgChartDB.clear()

      removeAllAppUserKeys()

      removeEnvParam('provider')
      removeEnvParam('userEmail')
      removeEnvParam('appType')
      removeEnvParam('apiURL')

      store.remove('app.user.providerToken')

      if (typeof onAfterLogout === 'function') {
        onAfterLogout()
      }

      return true
    })
    .catch((error) => {
      console.log('logout error', error)
    })
    .finally(() => {
      syncAniObj.destroy()
    })
}

export function notMatchingAccount(tokenEmail) {
  return confirmBase({
    icon: 'red',
    width: 450,
    title: i18n.t('login.button.differentAccount'),
    content: i18n.t('login.error.notmatchingOfficeNext', { email: tokenEmail }),
    okText: i18n.t('logOut.title.logout'),
    onOk: () => performSsoLogout(),
  })
}

export function startLanding(email, authUrl, isSso) {
  const promise = new Promise((resolve, reject) => {
    const onSuccessLanding = async (e) => {
      console.log(' -- message : ', e)

      if (window.location.origin === e.origin || e.origin.indexOf('officepassword.kr') < 0) {
        return
      }

      const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data

      closeLandingPopup()

      if (data.params.result === 'OK') {
        console.log(' -- params : ', data.params)

        const { jwt, providerToken } = handleAuthSuccess(data.params, email)

        if (jwt != null) {
          const tokenEmail = parsedJWT(jwt.accessToken)
          if (tokenEmail !== email && isSso !== 'sso') {
            notMatchingAccount(tokenEmail)
          } else if (jwt.accessToken != null) {
            window.removeEventListener('message', onSuccessLanding)
            saveTokenAndDispatchLogin(jwt, providerToken)
          } else if (data.params.verify_string != null) {
            window.removeEventListener('message', onSuccessLanding)
            resolve(data.params.verify_string)
          }
        }
      } else if (data.error != null) {
        console.log(' --- login error ', data)

        window.removeEventListener('message', onSuccessLanding)
        handleAuthError(
          data.params,
          email,
          notMatchingAccount,
          // 계약 만료(402) 안내 모달 확인 시 officenext 로그아웃(같은 파일 performSsoLogout).
          // (performSsoLogout이 내부에서 providerToken을 제거하므로 별도 콜백 불필요)
          () => performSsoLogout(),
        )
        reject(new Error('auth_error'))
      } else {
        reject(new Error('message parameter error.'))
      }
    }

    window.addEventListener('message', onSuccessLanding, false)

    prevLandingInfo.clearLandingEvent = () => {
      window.removeEventListener('message', onSuccessLanding)
      prevLandingInfo.clearLandingEvent = null
    }

    if (window.loginWin) {
      window.loginWin.location.href = authUrl
      window.loginTimer = setInterval(() => {
        if (window.loginWin?.closed) {
          clearInterval(window.loginTimer)
        }
      }, 1000)
    } else {
      // Safari의 경우 현재 창에서 직접 이동
      window.location.href = authUrl
    }
  })

  return promise
}

export function loginByToken(info) {
  const promise = new Promise((resolve) => {
    if (info.provider_token != null) {
      const userEmail = store.get('app.user.email')

      store.set('app.user.providerToken', info.provider_token)

      const appConfigData = appConfigMap[userEmail]

      if (appConfigData != null) {
        if (appConfigData.officenext === true) {
          // package 정보 - free, pro
          store.set('app.user.officenext', appConfigData.officenext)
        } else {
          store.remove('app.user.officenext')
        }
      } else {
        store.set('app.user.officenext', true)
      }
    }

    resolve('success')
  })

  return promise
}

export async function umkLogin(password, preloadedLoginData = null) {
  const reduxState = reduxStore.getState()
  const { umkDuration: duration, umkWarningDuration: warningDuration } = reduxState.settings
  const inviteType = JSON.parse(getEnvParam('inviteType') || '{}')

  setTempEnvParam('decryptedUMK', password)

  // preloadedLoginData: Passkey 경로에서 /v1/auth/passkey/login 응답을 전달하면
  // RSAEncDec.login 이 중복 /v1/auth/login 호출을 skip 한다.
  const rsaObj = new RSAEncDec({
    umk: password,
    isLogin: true,
    duration,
    warningDuration,
    inviteRegisterLogin: inviteType.flow_type === 'register' || inviteType.flow_type === 'signup',
    preloadedLoginData,
  })

  try {
    const ret = await rsaObj.init()

    if (ret !== true) {
      errorBase({
        title: i18n.t('login.error.invalidMasterPWTitle'),
        content: i18n.t('login.error.invalidMasterPW'),
      })

      if (ret === 'expired') {
        history.push('/expired')
      }
    } else {
      setTempEnvParam('rsaObj', rsaObj)

      if (inviteType.flow_type === 'register' || inviteType.flow_type === 'signup') {
        setTempEnvParam('inviteRegisterLogin', true)
        removeEnvParam('inviteType')

        reduxStore.dispatch({
          type: 'user/LOAD_CURRENT_ACCOUNT',
        })
      }
    }

    return Promise.resolve(ret)
  } catch (e) {
    if (e?.code === 'ACCESS_BLOCKED') {
      // 접근 차단(870/879) — pUtils.login이 이미 차단 페이지로 이동시킴, 에러 팝업 중첩 금지
      return Promise.resolve(null)
    }
    console.log(' - umkLogin error : ', e)

    errorBase({
      title: 'Error',
      content: i18n.t(`workspace.error.${e.code || 'null'}`),
    })
    return Promise.resolve(null)
  }
}

export async function umkRegister(password) {
  console.log(' --- umkRegister : ', password)
  const reduxState = reduxStore.getState()
  const { umkDuration: duration, umkWarningDuration: warningDuration } = reduxState.settings

  try {
    const rsaObj = new RSAEncDec({ umk: password, isRegister: true, duration, warningDuration })
    const ret = await rsaObj.init()

    if (ret !== true) {
      errorBase({
        title: i18n.t('admin.activity.failedLogin'),
        content: i18n.t('login.error.noMatchingPassword'),
      })
    } else {
      setTempEnvParam('rsaObj', rsaObj)
    }

    return Promise.resolve(true)
  } catch (e) {
    errorBase({
      title: i18n.t('basic.login.failedRegisterTitle'),
      content: i18n.t(`workspace.error.${e.code || 'null'}`),
    })
    console.log(' - umkRegister error : ', e)

    return Promise.resolve(false)
  }
}

export async function umkChangePW(password, newPassword, wasTemp = false, fromSettings = false) {
  // PR3 §7.3: wasTemp는 changePW 회전 분기로 전달(기본 false).
  // fromSettings: Settings 자발적 변경에서만 changePW가 828을 "잠금화면 재설정 유도" 안내로 표시.
  console.log(' --- umkChangePW : ', password, newPassword)

  let rsaObj = getTempEnvParam('rsaObj')
  const authType = getTempEnvParam('authType')

  let ret = false

  const reduxState = reduxStore.getState()
  const { umkDuration: duration, umkWarningDuration: warningDuration } = reduxState.settings

  try {
    setTempEnvParam('decryptedUMK', password)

    if (authType === 'change-password' || authType === 'login') {
      const base64PrivateKey = getTempEnvParam('base64privateKey')
      if (!base64PrivateKey) {
        // 키 미로드(선로딩 실패/미완료) — 빈 키로 changePW 진입 시 forge AES-CBC 0바이트 IV 복호 실패 →
        // catch 가 code 없는 raw Error 를 "현재 마스터 비밀번호 불일치"로 오안내한다. 조기 차단 + 정확 안내.
        // loadKeyFailed 는 모달/페이지 등 5곳에서 message.error 토스트로 노출 → 동일 메커니즘으로 통일
        // (errorBase 모달은 실제 changePW 실패 catch 용으로 보존 — 전제조건 가드=토스트 / 동작실패=모달).
        message.error(i18n.t('common.recovery.loadKeyFailed'))
        return Promise.resolve(false)
      }
      rsaObj = new RSAEncDec({
        umk: newPassword,
        base64PrivateKey,
        duration,
        warningDuration,
      })
      ret = await rsaObj.changePW(newPassword, true, wasTemp, fromSettings)
    } else {
      // eslint-disable-next-line no-lonely-if
      if (rsaObj == null) {
        rsaObj = new RSAEncDec({
          umk: newPassword,
          isLogin: true,
          duration,
          warningDuration,
        })
        ret = await rsaObj.init()

        if (ret === true) {
          ret = await rsaObj.changePW(newPassword, false, wasTemp, fromSettings)
        }
      } else {
        const tempRSAObj = new RSAEncDec({
          umk: newPassword,
          isLogin: true,
          duration,
          warningDuration,
        })
        ret = await tempRSAObj.init()

        if (ret === true) {
          ret = await tempRSAObj.changePW(newPassword, false, wasTemp, fromSettings)

          if (ret === true) {
            rsaObj = tempRSAObj
          }
        }
      }
    }

    if (ret !== true) {
      console.log(' --- ret: ', ret)
    } else {
      setTempEnvParam('rsaObj', rsaObj)
    }
  } catch (e) {
    console.log(' - umkChangePW error : ', e)
    // e.message 를 번역 키로 쓰면 crypto 원시 메시지("invalid padding ...")가 그대로
    // 노출된다. 복호화 실패 = 현재 마스터 비밀번호 불일치가 대부분이므로 안내 문구로 치환.
    // 서버 에러(e.code 보유)만 workspace.error.{code} 키로 번역.
    errorBase({
      title: i18n.t('login.error.changeMasterPWFailed'),
      content: e?.code
        ? i18n.t([`workspace.error.${e.code}`, 'login.error.invalidMasterPW'])
        : i18n.t('login.error.invalidMasterPW'),
    })
    return Promise.resolve(false)
  }

  return Promise.resolve(ret)
}

export async function getSettings() {
  const settings = await SettingsService.get()
  const { data: settingsData, code } = settings

  if (code === 200) {
    const settingValue = (label) => {
      return settingsData?.global_setting.filter((item) => item.label === label)[0]?.content
    }

    const zeroDarkWebSettings = settingsData?.personal_setting.reduce((acc, item) => {
      acc[item.label] = item.content
      return acc
    }, {})

    const locale = settingValue('language')
    const umkDuration = settingValue('auto_lock')
    const recoverKeyEnabled = settingValue('send_recovery_key_to_email')
    const isSendNotification = settingValue('send_notification_confirmation')
    const clipboardClear = settingValue('clipboard_clear')

    reduxStore.dispatch({
      type: 'settings/SET_SETTINGS',
      payload: {
        locale,
        umkDuration: umkDuration ? Number(umkDuration) * 60 * 1000 : 10 * 60 * 1000,
        recoverKeyEnabled: recoverKeyEnabled === 'email' ? 'email' : 'local',
        isSendNotification: isSendNotification === 'true',
        // clipboard_clear 미존재(기존 사용자) → 0(Never). 분 문자열 → ms.
        // 주의: "0" 은 truthy string 이라 ternary 의 true 분기로 가지만 Number("0")*60000=0 으로 정상 처리됨.
        clipboardClearMs: clipboardClear ? Number(clipboardClear) * 60 * 1000 : 0,
      },
    })

    reduxStore.dispatch({
      type: 'settings/CHANGE_SETTING',
      payload: {
        setting: 'zeroDarkWebSettings',
        value: zeroDarkWebSettings,
      },
    })

    if (zeroDarkWebSettings?.zdw_active === 'true') {
      reduxStore.dispatch({
        type: 'settings/CHANGE_SETTING',
        payload: {
          setting: 'showZeroDarkWeb',
          value: true,
        },
      })
    }

    return true
  }
  return false
}

export async function logout(options = {}) {
  deleteUMKInfo()

  // 익스텐션이 트리거한 로그아웃은 background 가 이미 /auth/logout 을 invalidate
  // 한 뒤라 여기서 재호출하면 401 만 받고 한 박자 늦어진다. skipApi 면 API
  // 호출을 건너뛰고 cleanup 만 즉시 수행.
  const skipApi = options.skipApi === true

  const syncAniObj = skipApi
    ? null
    : openloadingAni({
        text: i18n.t('logOut.desc.progress', { defaultValue: 'Loading...' }),
      })

  let samlRedirectUrl = null

  try {
    if (!skipApi) {
      const providerToken = store.get('app.user.providerToken')
      const officenext = store.get('app.user.officenext')

      if (officenext === true && providerToken != null) {
        const headers = {
          Accept: 'application/json',
        }
        await officeNextClient.post('/api/logout', {}, { headers })
      }

      if (isGlobal() && store.get('app.user.token')) {
        const token = store.get('app.user.token')
        let isSamlUser = false
        try {
          const payload = JSON.parse(atob(token.split('.')[1]))
          isSamlUser = payload.provider === 'saml'
        } catch {
          // JWT 파싱 실패 시 basic으로 처리
        }

        if (isSamlUser) {
          // SP-Initiated SLO: 전체 토큰 revoke + IdP LogoutRequest URL 반환
          const loginPageUrl = `${window.location.origin}/auth/login`
          const resp = await authClientRequiredToken.post(
            `/saml/slo?relay_state=${encodeURIComponent(loginPageUrl)}`,
          )
          samlRedirectUrl = resp?.data?.data?.redirect_url || null
        } else {
          await authClientRequiredToken.post('/auth/logout', {
            access_token: token,
            refresh_token: store.get('app.user.refreshToken'),
            service_group_code: 'safebox',
            provider: 'local',
          })
        }
      }
    }
  } catch (e) {
    console.log('logout error', e)
  } finally {
    syncAniObj?.destroy()
    await orgChartDB.clear()
  }

  // 로그아웃 시 대기 중인 클립보드 자동삭제를 즉시 수행(safebox 복사 비밀번호 잔류 방지).
  // redirect/키 정리 전에 writeText('') 완료를 보장하기 위해 await.
  await clearClipboardNow()

  // localStorage 정리 — redirect 전에 수행 (redirect 후에는 실행 불가)
  await Promise.all([
    removeAllAppUserKeys(),
    store.remove('app.settings.showZeroDarkWeb'),
    store.remove('app.settings.zeroDarkWebSettings'),

    removeEnvParam('inviteType'),
    removeEnvParam('provider'),
    removeEnvParam('userEmail'),
    removeEnvParam('appType'),
    removeEnvParam('apiURL'),
    removeEnvParam('verification_email'),
    removeEnvParam('expiredData'),
    removeEnvParam('common_auth_changePW'),
    removeTempEnvParam('verification_email'),
  ])

  // SAML SLO: IdP 로그아웃 페이지로 리다이렉트
  if (samlRedirectUrl) {
    window.location.href = samlRedirectUrl
    return true
  }

  return true
}
